"use client";

import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";

export interface StoryCard {
  slug: string;
  title: string;
  /** ISO date string, e.g. "2026-06-21". */
  date: string;
  readingMinutes: number;
}

interface StoriesSectionProps {
  stories: StoryCard[];
}

/**
 * Homepage "Stories" section: the latest blog posts + a link to all of them.
 * Posts are passed in from the server page (EN home → EN posts, SL home → SL
 * posts); language for labels and hrefs follows the route-forced locale.
 */
export function StoriesSection({ stories }: StoriesSectionProps) {
  const { lang } = useTranslation();
  const en = lang === "en";
  if (!stories.length) return null;

  const base = en ? "/blog/" : "/sl/blog/";
  const allHref = en ? "/blog" : "/sl/blog";
  const locale = en ? "en-US" : "sl-SI";
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });

  return (
    <section className="relative z-[2] mx-auto max-w-[1120px] px-6 md:px-12 py-24 md:py-[120px]">
      <div className="mb-9 flex items-end justify-between gap-6 border-b border-[var(--rule)] pb-5">
        <div>
          <p className="kicker-mono mb-3 inline-flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
            {en ? "STORIES · WRITING" : "ZGODBE · PISANJE"}
          </p>
          <h2 className="font-display text-[clamp(28px,4vw,40px)] leading-[1.05] tracking-[-0.02em] text-[var(--ink)]">
            {en ? "Latest " : "Zadnje "}
            <em className="font-medium italic text-[var(--terracotta)]">{en ? "stories" : "zgodbe"}</em>
          </h2>
        </div>
        <Link
          href={allHref}
          className="shrink-0 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)] underline-offset-4 transition hover:text-[var(--terracotta)] hover:underline"
        >
          {en ? "All stories →" : "Vse zgodbe →"}
        </Link>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stories.map((post) => (
          <Link
            key={post.slug}
            href={`${base}${post.slug}`}
            className="group flex min-h-[200px] flex-col rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/40 p-6 transition hover:-translate-y-0.5 hover:border-[color-mix(in_srgb,var(--terracotta)_45%,transparent)] hover:bg-[var(--paper-2)]/70"
          >
            <div className="mb-3 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
              <time dateTime={post.date}>{fmt(post.date)}</time>
              <span className="opacity-40">·</span>
              <span>
                {post.readingMinutes} {en ? "min read" : "min branja"}
              </span>
            </div>
            <h3 className="font-display text-[21px] leading-[1.25] tracking-[-0.01em] text-[var(--ink)] transition group-hover:text-[var(--terracotta)]">
              {post.title}
            </h3>
            <span className="mt-auto pt-5 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)] transition group-hover:text-[var(--terracotta)]">
              {en ? "Read →" : "Preberi →"}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
